import React, { useState } from 'react';
import {
  Check,
  X,
  Sparkles,
  AlertCircle,
  Sunrise,
  Sun,
  Moon,
  Map,
  Train,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

// --- LIFESTYLE SECTION ---
export const LifestyleSection: React.FC<{ data: LivingInAreaData['lifestyleOverview'] }> = ({
  data,
}) => {
  return (
    <section className="w-full max-w-5xl mx-auto py-16 px-6 bg-white">
      <div className="text-center space-y-4 mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-black uppercase tracking-widest">
          <Sparkles size={14} /> Area Insights
        </span>
        <h2 className="text-4xl font-black text-slate-900 md:text-5xl">
          Lifestyle <span className="text-blue-600">Compatibility</span>
        </h2>
        <p className="text-xl font-bold text-slate-700 max-w-2xl mx-auto leading-relaxed">
          Discover if the rhythm of this neighborhood matches your daily flow.
        </p>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        {/* Pros */}
        <div className="rounded-[2.5rem] border-2 border-slate-200 bg-slate-50 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-600 text-white shadow-md">
              <Check size={24} strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Perfect For</h3>
          </div>
          <ul className="space-y-5">
            {data.bestFor.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-slate-800 font-bold text-lg leading-snug"
              >
                <Check className="mt-1 h-5 w-5 shrink-0 text-green-700" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Cons */}
        <div className="rounded-[2.5rem] border-2 border-slate-200 bg-slate-50 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-600 text-white shadow-md">
              <AlertCircle size={24} strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Trade-offs</h3>
          </div>
          <ul className="space-y-5">
            {data.notIdealFor.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-slate-800 font-bold text-lg leading-snug"
              >
                <X className="mt-1 h-5 w-5 shrink-0 text-orange-700" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

// --- DAY IN THE LIFE SECTION ---
export const DayInLifeSection: React.FC<{
  data: LivingInAreaData['dayInTheLife'];
  areaName: string;
}> = ({ data, areaName }) => {
  const timeSlots = [
    {
      title: 'Morning',
      text: data.morning,
      time: '6:00 AM - 11:00 AM',
      color: 'text-amber-900',
      bg: 'bg-amber-100',
      icon: <Sunrise />,
    },
    {
      title: 'Daytime',
      text: data.daytime,
      time: '11:00 AM - 5:00 PM',
      color: 'text-blue-900',
      bg: 'bg-blue-100',
      icon: <Sun />,
    },
    {
      title: 'Evening',
      text: data.evening,
      time: '5:00 PM - onwards',
      color: 'text-indigo-900',
      bg: 'bg-indigo-100',
      icon: <Moon />,
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-16">
        <h2 className="text-5xl font-black text-slate-900 tracking-tight">
          A Day in <span className="text-teal-700">{areaName}</span>
        </h2>
      </div>

      <div className="grid gap-8 md:grid-cols-3">
        {timeSlots.map((slot) => (
          <div
            key={slot.title}
            className="rounded-[2.5rem] border-2 border-slate-200 bg-white p-8 shadow-lg"
          >
            <div className="flex items-center gap-4 mb-6">
              <div
                className={`h-14 w-14 flex items-center justify-center rounded-2xl ${slot.bg} ${slot.color}`}
              >
                {React.cloneElement(slot.icon, { size: 28, strokeWidth: 2.5 })}
              </div>
              <div>
                <p className={`text-sm font-black uppercase tracking-widest ${slot.color}`}>
                  {slot.title}
                </p>
                <p className="text-xs font-black text-slate-500">{slot.time}</p>
              </div>
            </div>
            <p className="text-lg font-bold leading-relaxed text-slate-800">{slot.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// --- TRANSPORT SECTION (STAT CARD SUB-COMPONENT) ---
const TransportStatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ icon, label, value, highlight }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const requiresToggle = value.length > 140;

  return (
    <div
      className={`flex flex-col p-6 rounded-2xl border-2 transition-all ${highlight ? 'bg-green-50 border-green-200 text-green-900' : 'bg-white border-slate-200 text-slate-800'}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={highlight ? 'text-green-700' : 'text-indigo-700'}>{icon}</div>
        <span className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div
        className={`relative overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[500px]' : 'max-h-24'}`}
      >
        <p className="text-base font-bold leading-relaxed">{value}</p>
        {!isExpanded && requiresToggle && (
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white via-white/80 to-transparent" />
        )}
      </div>
      {requiresToggle && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 flex items-center gap-1 text-sm font-black text-indigo-700 hover:text-indigo-900"
        >
          {isExpanded ? 'Show Less' : 'Read More'}{' '}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      )}
    </div>
  );
};

export const TransportSection: React.FC<{ data: LivingInAreaData['gettingAround'] }> = ({
  data,
}) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-white border-2 border-slate-200 shadow-2xl">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-1/3 p-10 md:p-12 text-white bg-gradient-to-br from-indigo-800 via-blue-900 to-slate-900">
            <div className="relative z-10 space-y-8">
              <div className="flex items-start gap-4">
                <Map size={40} className="text-blue-300" />
                <h2 className="text-4xl font-black leading-tight">Getting Around</h2>
              </div>
              <p className="text-2xl font-medium italic text-slate-50 border-l-4 border-blue-400 pl-6">
                "{data.insight}"
              </p>
            </div>
          </div>
          <div className="lg:w-2/3 p-8 md:p-12 bg-slate-50/50 space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TransportStatCard
                icon={data.carFreeFriendly ? <CheckCircle /> : <XCircle />}
                label="Car-Free Living"
                value={data.carFreeFriendly ? 'Viable for local trips.' : 'Car recommended.'}
                highlight={data.carFreeFriendly}
              />
              <TransportStatCard
                icon={<Train />}
                label="Public Transport"
                value={data.publicTransportUsage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
